/**
 * Custom element for searching and paginating shelters during an active shelter challenge.
 *
 * Responsibilities:
 * - Submits search form data to the shelter API and renders result cards.
 * - Manages pagination and placeholder UI during async operations.
 * - Injects shelter info into a cloned template for each result.
 * - Appends `<cusa-shelter-challenge-voting-button>` elements to each result card, setting the per-result shelter id.
 *
 * Requirements:
 * - Template DOM must include a template with `id="shelter-card-template"` and child elements matching expected class selectors.
 * - The challenge id/handle are supplied by `cusa-shelter-challenge-voting-button.liquid` at render time.
 */
class CusaShelterChallangeSearch extends HTMLElement {
	constructor() {
		super();
		this.formEl = this.querySelector('form');
		this.templateEl = this.querySelector('#shelter-card-template');
		this.resultsCountEl = this.querySelector('#results-count');
		this.resultsEl = this.querySelector('#shelter-results');
		this.resultsPaginationEl = this.querySelector('#shelter-results-pagination');
		this.caretTemplateEl = this.querySelector('#caret-template');

		this.END_POINT = '/apps/takeaction/shelters';

		this.fallbackImg = `${this.dataset.fallbackImage}`;

		this.shelterData = null;
		this.currentPage = 1;
		this.numOfPaginationSteps = 3; // The final number of links is always (this * 2) + 1. This number also includes the first and last page links.
		this.prevPageText = 'Previous page';
		this.nextPageText = 'Next page';
	}

	connectedCallback() {
		this.setupForm();

		if (!this.resultsEl) {
			this.resultsEl = document.createElement('div');
			this.resultsEl.id = 'shelter-results';
			this.appendChild(this.resultsEl);
		}
	}

	/**
	 * Sets up form submission handling for search input.
	 * Prevents default form behavior and initiates shelter data fetch.
	 */
	setupForm = () => {
		if (!this.formEl) return;

		this.formEl.addEventListener('submit', (e) => {
			e.preventDefault();
			this.fetchShelterData(this.END_POINT);
		});
	};

	/**
	 * Scrolls the page to the top of the search component.
	 */
	updateResults = () => {
		scrollToElement(document.querySelector('cusa-shelter-challange-search').offsetTop);
	};

	/**
	 * Handles pagination link click and triggers data fetching for the selected page.
	 * @param {MouseEvent} e - The click event on a pagination link.
	 */
	paginationClick = (e) => {
		e.preventDefault();
		const clickedPageLinkHref = new URL(e.target.href);
		this.currentPage = clickedPageLinkHref.searchParams.get('page');
		this.fetchShelterData(e.target.href);
		this.updateResults();
	};

	/**
	 * Resets the shelter results container and displays loading placeholders.
	 */
	resetResults = () => {
		this.resultsEl.innerHTML = '';
		this.resultsCountEl.textContent = '';
		for (let i = 0; i < 24; i++) {
			const placeholderCardEl = document.importNode(this.templateEl.content, true);
			placeholderCardEl.firstElementChild.classList.add('placeholder-card');
			placeholderCardEl.querySelector('img').remove();
			placeholderCardEl.querySelector('button').disabled = true;
			this.resultsEl.appendChild(placeholderCardEl);
		}
		this.resultsEl.classList.remove('hidden');
		this.resultsEl.classList.remove('no-results');
	};

	/**
	 * Fetches shelter data from the server using form input values.
	 * Populates results and pagination on success, or displays error on failure.
	 * @param {string} endPoint - The endpoint URL to POST data to.
	 */
	fetchShelterData = async (endPoint) => {
		this.resetResults();

		try {
			const formData = new FormData(this.formEl);
			const jsonData = Object.fromEntries(formData.entries());

			const response = await loggedFetch(endPoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(jsonData)
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			this.shelterData = await response.json();
			console.log('Received Data:', this.shelterData);

			// Ensure data is received before proceeding
			if (this.shelterData?.data?.length) {
				this.displayShelterResults();
				this.currentPage = this.shelterData.paging.current_page;
				this.createPagination(this.shelterData.paging.total);
			} else {
				this.resultsEl.innerHTML = 'No results found. Please try again.';
				this.resultsEl.classList.remove('hidden');
				this.resultsEl.classList.add('no-results');
				this.resetPagination();
			}
			this.resultsCountEl.textContent = `${Number(this.shelterData.paging.total).toLocaleString()} Result${
				this.shelterData.paging.total !== 1 ? 's' : ''
			}`;
		} catch (error) {
			console.error('Error fetching data:', error);
			const specificName = error?.constructor?.name && error.constructor.name !== 'Error' ? error.constructor.name : null;
			window.logFrontendError?.({
				error_message: error?.message,
				error_type: specificName || 'ShelterFetchError',
				error_stack: error?.stack,
				context: { endpoint: endPoint }
			});
			this.resultsEl.innerHTML = 'Error fetching shelter results. Please try again.';
		}
	};

	/**
	 * Clones the caret SVG from the template and applies direction-specific class.
	 * @param {string} direction - Either 'Previous page' or 'Next page'.
	 * @returns {SVGElement} The directional SVG element.
	 */
	getCaretSvgMarkup = (direction) => {
		if (!this.caretTemplateEl) return;

		const clone = document.importNode(this.caretTemplateEl.content, true);
		const svg = clone.querySelector('svg');

		svg.classList.add(direction === this.prevPageText ? 'prev' : 'next');

		return svg;
	};

	/**
	 * Generates an array of page numbers including dynamic ellipses.
	 * Ensures first and last pages are always included.
	 * @param {number} currentPageNum - The current page.
	 * @param {number} range - How many surrounding pages to include.
	 * @param {number} [maxNumber=Infinity] - The highest valid page number.
	 * @returns {(number|string)[]} An array of page numbers and ellipses.
	 */
	generatePageNumsArray = (currentPageNum, range, maxNumber = Infinity) => {
		let result = [];

		// Adjust range dynamically when near edges
		let adjustedRange = range;

		if (currentPageNum - range < 1) {
			adjustedRange += Math.abs(currentPageNum - range - 1);
		}

		if (currentPageNum + range > maxNumber) {
			adjustedRange += currentPageNum + range - maxNumber;
		}

		for (let i = 0; i < adjustedRange; i++) {
			if (currentPageNum - i >= 1) result.push(currentPageNum - i);
			if (currentPageNum + i <= maxNumber) result.push(currentPageNum + i);
		}

		// Always include first and last page
		result.push(1, maxNumber);

		// Sort and remove duplicates
		result = Array.from(new Set(result.sort((a, b) => a - b)));

		// Inject ellipses where needed
		let finalResult = [];

		for (let i = 0; i < result.length; i++) {
			if (i > 0 && result[i] - result[i - 1] > 1) {
				finalResult.push('…');
			}
			finalResult.push(result[i]);
		}

		return finalResult;
	};

	/**
	 * Clears and hides the pagination element.
	 */
	resetPagination = () => {
		this.resultsPaginationEl.innerHTML = '';
		this.resultsPaginationEl.classList.add('hidden');
	};

	/**
	 * Renders pagination links based on the total result count.
	 * @param {number} totalResults - Total number of available shelter results.
	 */
	createPagination = (totalResults) => {
		this.resetPagination();
		if (totalResults <= 24) return;

		const totalPages = Math.round(Math.floor(totalResults / 24));
		this.resultsPaginationEl.innerHTML = '';

		let pageNums = this.generatePageNumsArray(this.currentPage, this.numOfPaginationSteps, totalPages);

		pageNums.unshift(this.prevPageText);
		pageNums.push(this.nextPageText);

		console.log(pageNums);

		for (let i = 0; i < pageNums.length; i++) {
			let pageLink = null;
			const pageNum = pageNums[i];

			if (
				(i === 0 && this.currentPage === 1) ||
				(i === pageNums.length - 1 && this.currentPage === totalPages) ||
				pageNums[i] === '…'
			) {
				pageLink = document.createElement('span');
				pageNums[i] !== '…' ? pageLink.classList.add('disabled') : null;
			} else {
				pageLink = document.createElement('a');
				if (i === 0) {
					pageLink.href = `${this.END_POINT}?page=${this.currentPage - 1}`;
				} else if (i === pageNums.length - 1) {
					pageLink.href = `${this.END_POINT}?page=${this.currentPage + 1}`;
				} else {
					pageLink.href = `${this.END_POINT}?page=${pageNum}`;
				}
				let linkText = typeof pageNum === 'number' ? `Page ${pageNum} of ${totalPages}` : pageNum;
				pageLink.title = linkText;
				pageLink.ariaLabel = linkText;
				pageLink.addEventListener('click', (e) => {
					this.paginationClick(e);
				});
			}

			if (typeof pageNum === 'number' || pageNum === '…') {
				pageLink.appendChild(document.createTextNode(pageNum));
			}

			if (pageNums[i] === this.prevPageText || pageNums[i] === this.nextPageText) {
				const svgMarkup = this.getCaretSvgMarkup(pageNums[i]);
				pageLink.appendChild(svgMarkup);
			}

			if (this.currentPage === pageNum) {
				pageLink.classList.add('current-page', 'button');
			} else {
				pageNums[i] !== '…' ? pageLink.classList.add('button', 'button--secondary') : null;
			}

			this.resultsPaginationEl.appendChild(pageLink);
			this.resultsPaginationEl.classList.remove('hidden');
		}
	};

	/**
	 * Iterates over fetched shelter data and injects result cards into the DOM.
	 */
	displayShelterResults = () => {
		this.resultsEl.innerHTML = '';
		const results = this.shelterData.data;

		if (!results.length) {
			this.resultsEl.innerHTML = 'No results placeholder';
		} else {
			console.log('Results:', results);

			for (const shelterResult of results) {
				this.resultsEl.appendChild(this.createShelterResultCard(shelterResult));
			}
		}
	};

	/**
	 * Creates and returns a DOM node for a single shelter result card.
	 * @param {Object} shelterResult - Shelter object returned by the API.
	 * @returns {HTMLElement} A DOM node representing the shelter result.
	 */
	createShelterResultCard = (shelterResult) => {
		if (!this.templateEl) return;

		const clone = document.importNode(this.templateEl.content, true);
		const cloneLink = clone.querySelector('a');
		const cloneImg = clone.querySelector('img');
		const cloneName = clone.querySelector('.shelter-name');
		// const cloneAddress = clone.querySelector('.street-address');
		const cloneCity = clone.querySelector('.shelter-city');
		const cloneState = clone.querySelector('.shelter-state');
		// const cloneZip = clone.querySelector('.shelter-zip');
		// const cloneCountry = clone.querySelector('.shelter-country');
		const voteComponent = clone.querySelector('cusa-shelter-challenge-voting-button');

		// Update the anchor tag's href
		if (cloneLink) {
			cloneLink.href = `/pages/shelter/${shelterResult.handle}` || '#';
		}

		// Check if media_image exists before accessing its properties
		if (cloneImg && shelterResult.media_image) {
			cloneImg.src = shelterResult.media_image.url || '';
			cloneImg.alt = shelterResult.media_image.alt_text || 'No image available';
			cloneImg.width = shelterResult.media_image.width || 150; // Default width
			cloneImg.height = shelterResult.media_image.height || 150; // Default height
		} else if (cloneImg) {
			// Fallback in case there is no image
			cloneImg.src = this.fallbackImg;
			cloneImg.alt = 'No image available';
		}

		// Update clone name
		if (cloneName) cloneName.innerHTML = shelterResult.name || 'No Name Available';

		// Update clone address
		// if (cloneAddress) cloneAddress.textContent = shelterResult.address || 'No Address Provided';
		if (cloneCity) cloneCity.textContent = shelterResult.city || '';
		if (cloneState) cloneState.textContent = shelterResult.state || '';
		// if (cloneZip) cloneZip.textContent = shelterResult.zip_postal_code || '';
		// if (cloneCountry) cloneCountry.textContent = shelterResult.country || '';

		// Update voting component attributes. The challenge id/handle are baked into the
		// template by 'cusa-shelter-challenge-voting-button.liquid'; only the shelter id is
		// specific to each search result, so that's all we set here.
		if (voteComponent) {
			voteComponent.setAttribute('data-shelter-id', shelterResult.metaobject_id || '');
		}

		return clone;
	};
}

customElements.define('cusa-shelter-challange-search', CusaShelterChallangeSearch);
